import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, concat, map, of, switchMap } from 'rxjs';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPanelSectionScroller } from '../hd-panel-section-scroller';
import { HdIncarnationCross, HdProfile } from '../basic-properties/hd-chart-properties.types';
import { HD_PROFILE_LINE_KEYS } from '../basic-properties/hd-chart-properties.constants';
import { HdInfoSectionsComponent } from '../chart-basics-info/hd-info-sections.component';
import { HdInfoSectionVm, HdInfoSectionsVm } from '../chart-basics-info/hd-info-sections.models';
import { HdIncarnationCrossInfoService } from './hd-incarnation-cross-info.service';
import { HdCrossMarkdownParts, splitHdCrossMarkdown } from './hd-incarnation-cross-markdown.util';
import {
  HdIncarnationCrossPanelService,
  buildHdIncarnationCrossPanelKey,
  hdIncarnationCrossPlacementFrom,
} from './hd-incarnation-cross-panel.service';

/** Paneel-data: het kruis en, vanuit een chart, het profiel van die chart. */
export interface HdIncarnationCrossInfoPanelData {
  readonly cross: HdIncarnationCross;
  /**
   * Profiel van de chart: die profielsectie staat open, de andere dicht.
   * `null` (geopend vanuit de lijst van alle kruisen): alle profielen open.
   */
  readonly profile: HdProfile | null;
}

const PROPERTIES_PREFIX = 'humanDesign.properties';

/**
 * Info-paneel van één incarnatiekruis (één paneel per kruis, tab "IX 13/7 | 1/2").
 * Het kruisdocument (`crosses/{nl,en}/{angle}/{sleutel}.md`) wordt opgedeeld
 * rond de profielsecties: de profielen verschijnen als inklapbare lijst van
 * {@link HdInfoSectionsComponent}, met het profiel van de chart open. De knop
 * naast de titel opent het gedeelde paneel met de algemene uitleg. Elk nieuw
 * kruis of profiel zet het paneel terug bovenaan.
 */
@Component({
  selector: 'hd-incarnation-cross-info-panel',
  standalone: true,
  imports: [HdInfoSectionsComponent],
  template: '<hd-info-sections [vm]="vm()" (headerActionRequested)="openGeneral()" />',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIncarnationCrossInfoPanelComponent implements AfterViewChecked {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdIncarnationCrossInfoPanelData | null> | null;
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly infoService = inject(HdIncarnationCrossInfoService);
  private readonly panels = inject(HdIncarnationCrossPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly http = inject(HttpClient);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Terug naar boven bij een nieuw kruis. De gedeelde scroller houdt de
   * uitlijning even vast, ook wanneer de dock bij tab-activatie zijn bewaarde
   * scrollpositie terugzet.
   */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly cross = computed<HdIncarnationCross | null>(() => this.panelData?.()?.cross ?? null);

  private readonly profileKey = computed<string | null>(() => {
    const profile = this.panelData?.()?.profile;
    return profile ? `${profile.personalityLine}/${profile.designLine}` : null;
  });

  private readonly markdownUrl = computed<string | null>(() => {
    const cross = this.cross();
    return cross ? this.infoService.crossMarkdownUrl(cross, this.lang()) : null;
  });

  /** Opgedeeld kruisdocument; `null` zolang het (nieuwe) document laadt. */
  private readonly parts = toSignal(
    toObservable(this.markdownUrl).pipe(
      switchMap(url => url == null
        ? of(null)
        : concat(
          of(null),
          this.http.get(url, { responseType: 'text' }).pipe(
            map(markdown => splitHdCrossMarkdown(markdown)),
            catchError(() => of<HdCrossMarkdownParts>({ intro: '', profiles: [], outro: '' })),
          ),
        )),
    ),
    { initialValue: null },
  );

  protected readonly vm = computed<HdInfoSectionsVm>(() => {
    const lang = this.lang();
    const cross = this.cross();
    const parts = this.parts();
    const ownKey = this.profileKey();
    const title = cross?.name
      ? `${this.angleLabel(cross.angleKey, lang)} ${this.gateInfo.crossNameConnectorIn(lang)} ${this.gateInfo.crossNameIn(cross.name, lang)}`
      : this.text('title');
    const [pSun, pEarth, dSun, dEarth] = cross?.gates ?? [];
    const sections: HdInfoSectionVm[] = (parts?.profiles ?? []).map(profile => {
      const isOwn = profile.key === ownKey;
      return {
        key: profile.key,
        label: profile.key,
        subtitle: this.profileLineNames(profile.key, lang),
        markdown: profile.markdown,
        isOwn,
        badges: isOwn ? [this.text('ownProfileBadgeLabel')] : [],
        facts: [],
      };
    });
    const hasOwnSection = sections.some(section => section.isOwn);
    return {
      title,
      subtitle: cross ? `${pSun}/${pEarth} | ${dSun}/${dEarth}` : undefined,
      headerActionLabel: this.text('jumpToGeneralLabel'),
      generalMarkdown: parts?.intro,
      sections,
      outroMarkdown: parts?.outro,
      collapsible: true,
      initiallyExpanded: hasOwnSection && ownKey ? [ownKey] : sections.map(section => section.key),
      expansionKey: `${cross ? buildHdIncarnationCrossPanelKey(cross) : ''}|${ownKey ?? 'all'}|${sections.length}`,
    };
  });

  constructor() {
    // Elke (her)aanlevering van paneel-data (ander kruis, ander profiel) zet
    // het paneel terug bovenaan; het scrollen zelf gebeurt in ngAfterViewChecked.
    effect(() => {
      this.panelData?.();
      this.scroller.arm('.hd-info-sections');
    });
    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  /** Knop naast de titel: het gedeelde paneel met de algemene uitleg, in dezelfde zone. */
  protected openGeneral(): void {
    this.panels.openGeneral(hdIncarnationCrossPlacementFrom(this.panelRef));
  }

  /** "Rechte-Hoek Kruis" uit de properties-vertaling, in de contenttaal. */
  private angleLabel(angleKey: string, lang: string): string {
    return this.propertyText(`crossAngleValues.${angleKey}`, lang);
  }

  /** "Onderzoeker / Martelaar" voor profiel "1/3". */
  private profileLineNames(profileKey: string, lang: string): string {
    const names = profileKey.split('/')
      .map(line => this.propertyText(`profileLineNames.${HD_PROFILE_LINE_KEYS[Number(line) - 1]}`, lang));
    return names.every(Boolean) ? names.join(' / ') : '';
  }

  private propertyText(key: string, lang: string): string {
    const path = `${PROPERTIES_PREFIX}.${key}`;
    const translated = this.transloco.translate(path, undefined, lang);
    return translated === path ? '' : translated;
  }

  private text(key: string): string {
    return this.gateInfo.crossesInfoTextIn(key, this.lang());
  }
}
